'use client';

/* eslint-disable @next/next/no-assign-module-variable */
/* eslint-disable prefer-destructuring */
/* eslint-disable @typescript-eslint/no-use-before-define */

import { createFileForFormDocumentSubmission } from '@commenda/api/form-builder/files/createFile';
import {
  createFormSubmission,
  FormsSubmissionStatus,
  updateFormSubmission,
  useFormSubmission,
  type ICommendaOSWriteFailure,
} from '@commenda/api/form-builder/form-submissions';
import { useApiScope } from '@commenda/api/scope';
import {
  Button,
  CountryCode,
  Icons,
  showNotification,
} from '@commenda/kakashi';
import { FormSubmissionSourceType } from '@commenda/prisma';
import type {
  ComponentVisibility,
  DSLFile,
  FormComponent,
  FormDSL,
  FormDSLContext,
  FormModule,
  FormStep,
  ValidationRules,
} from '@commenda/types';
import type { DSLContext } from '@commenda/types/src/commenda-os-dsl';
import { useRouter } from 'next/router';
import { classNames } from 'node_modules/@commenda/utils';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CollapsibleModuleCard,
  NewIncorporationLayout,
  ProgressStepItem,
} from '../components/new-incorp-layout';
import { AddressInputComponent } from '../components/wrappers/AddressInput';
import { AlertComponent } from '../components/wrappers/Alert';
import { AutoDetectFieldComponent } from '../components/wrappers/AutoDetectField';
import { BankAccountComponent } from '../components/wrappers/BankAccount';
import { BusinessEntitySelectOrCreateComponent } from '../components/wrappers/BusinessEntitySelectOrCreate';
import { CheckboxGroupComponent } from '../components/wrappers/CheckboxGroup';
import { CountryInputComponent } from '../components/wrappers/CountryInput';
import { DateInputComponent } from '../components/wrappers/DateInput';
import { DirectorDocumentComponent } from '../components/wrappers/DirectorDocument';
import { DocumentComponent } from '../components/wrappers/Document';
import { DynamicFieldsComponent } from '../components/wrappers/DynamicFields';
import { EntitySelectComponent } from '../components/wrappers/EntitySelect';
import { EntitySelectOrCreateComponent } from '../components/wrappers/EntitySelectOrCreate';
import { FileUploadComponent } from '../components/wrappers/FileUpload';
import { FormSummaryComponent } from '../components/wrappers/FormSummary';
import { GovernmentIdentifierComponent } from '../components/wrappers/GovernmentIdentifier';
import { InputComponent } from '../components/wrappers/Input';
import { KeyPersonsComponent } from '../components/wrappers/KeyPersonsComponent';
import { LocationComponent } from '../components/wrappers/Location';
import { MultiFileUploadComponent } from '../components/wrappers/MultiFileUpload';
import { NestedFormComponent } from '../components/wrappers/NestedFormComponent';
import { NumberInputComponent } from '../components/wrappers/NumberInput';
import { IndirectTaxRegistrationComponent } from '../components/wrappers/IndirectTaxRegistration';
import { OwnershipComponent } from '../components/wrappers/Ownership';
import { PasswordInputComponent } from '../components/wrappers/PasswordInput';
import { PhoneInputComponent } from '../components/wrappers/PhoneInput';
import { RadioGroupComponent } from '../components/wrappers/RadioGroup';
import { RegisteredAgentFieldComponent } from '../components/wrappers/RegisteredAgentField';
import { RepeatableModuleRenderer } from '../components/wrappers/RepeatableModuleRenderer';
import { SelectComponent } from '../components/wrappers/Select';
import { SubFormDialog } from '../components/wrappers/SubFormDialog';
import { TextAreaComponent } from '../components/wrappers/TextArea';
import { UrlInputComponent } from '../components/wrappers/UrlInput';
import { PreviewTestingPanel } from '../preview/PreviewTestingPanel';
import {
  usePreviewTestRunner,
  type RunStatus,
  type StepRunReport,
} from '../preview/usePreviewTestRunner';
import { CommendaOSFailureHelper } from '../utils/commenda-os-failure-helper';
import { buildComponentMap, buildFieldLabelMap } from '../utils/dsl-traversal';
import { fieldTokenId } from '../utils/field-tokens';
import {
  findModuleIdForField,
  getDefaultExpandedModulesForStep,
  isComponentReadOnly,
  isInstanceFieldVisible,
  isValueEmpty,
  rewriteRulesForInstance,
} from '../utils/form-state';
import { isModuleLockingComponent } from '../types';
import { fieldValidationError, isRuleActive } from '../utils/validation';
import { ComponentRegistryProvider } from './ComponentRegistryContext';
import {
  FormContext,
  type FormContextType,
  type PreviewFillHandler,
} from './FormContext';
import {
  useCommendaOSPrefill,
  useCommendaOSSubmit,
} from './useCommendaOSIntegration';

// =============================================================================
// Types
// =============================================================================

// Props for DSLFormRenderer component
export interface DSLFormRendererProps {
  dsl: DSLFile;
  formTemplateId?: string;
  formTemplateKey?: string;
  existingSubmissionId?: string; // For editing existing submissions
  onSubmitSuccess?: (submissionId: string) => void | Promise<void>;
  onSaveSuccess?: (submissionId: string) => void;
  /** Called when a draft is auto-saved during step navigation (no modal close) */
  onDraftSaved?: (submissionId: string) => void;
  className?: string;
  hideTitle?: boolean;
  shouldUseContainer?: boolean;
  onSave?: (submissionId: string | null) => void;
  onClose?: () => void;
  companyId?: number;
  corporationId?: number;
  directorId?: number;
  serviceRequestId?: string;
  serviceRequestTaskId?: number;
  /**
   * Seed values for a fresh form (no existingSubmissionId). Used to prefill a
   * re-opened task after a workflow reset from its latest prior submission.
   * Saving creates a NEW submission, so prior submissions are preserved as history.
   */
  prefillFormData?: Record<string, unknown>;
  /** Header configuration */
  header?: {
    entityName?: string;
    countryCode?: string;
    onShare?: () => void;
    hideShareButton?: boolean;
    hideSaveAndCloseButton?: boolean;
    hideCloseButton?: boolean;
    /** Rendered in the top header next to the logo (e.g. preview corporation picker) */
    leftSlot?: React.ReactNode;
  };
  /** Hide the top header bar */
  hideTopHeader?: boolean;
  /**
   * DSL context for Commenda OS operations.
   * Contains entity IDs needed for reading/writing data via DSL.
   */
  dslContext?: FormDSLContext;
  /**
   * When true, the form is in preview mode. No data is written to the backend.
   */
  isPreview?: boolean;
  /**
   * Preview only: start "fill & run to end" as soon as the form is ready,
   * without waiting for the panel's button. Lets the builder's Runs tab drive
   * a test run.
   */
  autoStartTestRun?: boolean;
  /** Preview only: fires once a test run reaches done or halted. */
  onTestRunComplete?: (report: StepRunReport[], status: RunStatus) => void;
  /** Preview only: hide the floating testing panel (a host may show its own progress). */
  hideTestingPanel?: boolean;
}

// =============================================================================
// Helper Functions
// =============================================================================

// Helper to get nested form value by path (e.g., "saveKey.0.fieldId")
function getFormValueByPath(
  formData: Record<string, unknown>,
  path: string
): unknown {
  const parts = path.split('.');
  let current: unknown = formData;
  for (const part of parts) {
    if (current == null) return undefined;
    const num = parseInt(part, 10);
    if (!Number.isNaN(num) && part === String(num)) {
      current = (current as unknown[])?.[num];
    } else {
      current = (current as Record<string, unknown>)?.[part];
    }
  }
  return current;
}

// Helper function to format field IDs for display (used in some places)
const formatFieldId = (fieldId: string): string => {
  return fieldId
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/^\s/, '')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();
};

// Helper function to format values for display
const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'object') {
    if (value instanceof File) {
      return value.name;
    }
    if (Array.isArray(value)) {
      return value.length > 0 ? value.map(formatValue).join(', ') : '—';
    }
    // For address objects or other complex objects
    const obj = value as Record<string, unknown>;
    const parts = Object.entries(obj)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([k, v]) => `${formatFieldId(k)}: ${formatValue(v)}`);
    return parts.length > 0 ? parts.join(', ') : '—';
  }
  return String(value);
};

// Collect NestedForm fieldIds from DSL for validating restored nestedFormOpen
function getNestedFormFieldIds(dsl: DSLFile | undefined): Set<string> {
  const ids = new Set<string>();
  dsl?.steps?.forEach((step) => {
    step?.modules?.forEach((module) => {
      module?.components?.forEach((component) => {
        if (
          component.component === 'NestedForm' &&
          typeof component.fieldId === 'string'
        ) {
          ids.add(component.fieldId);
        }
      });
    });
  });
  return ids;
}

// Collect all bindings from DSL - maps sourceField -> [targetFields]
function collectBindings(dsl: DSLFile): Map<string, string[]> {
  const bindingsMap = new Map<string, string[]>();

  dsl?.steps?.forEach((step) => {
    step?.modules?.forEach((module) => {
      module?.components?.forEach((component) => {
        if (component.bindings?.sourceField && component.fieldId) {
          const sourceField = component.bindings.sourceField;
          const targetField = component.fieldId;
          const existing = bindingsMap.get(sourceField) || [];
          if (!existing.includes(targetField)) {
            existing.push(targetField);
          }
          bindingsMap.set(sourceField, existing);
        }
      });
    });
  });

  return bindingsMap;
}

// Collect fields that should be disabled due to bindings
function collectDisabledBindings(dsl: DSLFile): Set<string> {
  const disabledFields = new Set<string>();

  dsl?.steps?.forEach((step) => {
    step?.modules?.forEach((module) => {
      module.components.forEach((component) => {
        if (
          component.bindings?.sourceField &&
          component.bindings.disableOnBind &&
          component.fieldId
        ) {
          disabledFields.add(component.fieldId);
        }
      });
    });
  });

  return disabledFields;
}

// Map internal field keys to human‑readable labels for backend
function mapFormDataForBackend(
  formData: Record<string, unknown>,
  fieldLabelMap: Record<string, string>
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  Object.entries(formData).forEach(([key, value]) => {
    if (value === undefined) return;

    const labelPath = fieldLabelMap[key];

    if (!labelPath) {
      // no mapping → keep original key (or transform it if you want)
      result[key] = value;
      return;
    }

    // labelPath is like "Step 1.Personal.Text Input"
    // we want to KEEP the entire thing, just replace spaces with underscores:
    // "Step_1.Personal.Text_Input"
    const withStep = labelPath;
    const backendKey = withStep.replace(/\s+/g, '_');

    result[backendKey] = value;
  });

  return result;
}

/** Resolve default expression tokens: $today, $now, $field:fieldId, $context:key */
function resolveDefaultExpression(
  expr: string,
  formData: Record<string, unknown>,
  dslContext?: Record<string, unknown>
): unknown {
  if (expr === '$today') return new Date().toISOString().split('T')[0];
  if (expr === '$now') return new Date().toISOString();
  const fieldId = fieldTokenId(expr);
  if (fieldId) return formData[fieldId];
  if (expr.startsWith('$context:')) return dslContext?.[expr.slice(9)];
  return expr;
}

// Collect default values from DSL (optional dslContext for $context: and $today/$now in defaultExpression)
function collectDefaultValues(
  dsl: DSLFile,
  dslContext?: Record<string, unknown>
): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};
  const formDataForCollect = {} as Record<string, unknown>;

  dsl?.steps?.forEach((step) => {
    step?.modules?.forEach((module) => {
      const repeatable = module.repeatable as
        | { enabled: boolean; min: number }
        | undefined;
      const saveKey = module.saveKey || module.id;

      const resolveDefault = (component: FormComponent): unknown => {
        if (component.defaultValue !== undefined) return component.defaultValue;
        if (component.defaultExpression) {
          return resolveDefaultExpression(
            component.defaultExpression,
            formDataForCollect,
            dslContext
          );
        }
        return undefined;
      };

      if (repeatable?.enabled) {
        const minInstances = Math.max(1, repeatable.min);
        const instanceDefaults = module.components.reduce(
          (acc, component) => {
            if (component.fieldId) {
              const v = resolveDefault(component);
              if (v !== undefined) acc[component.fieldId] = v;
            }
            return acc;
          },
          {} as Record<string, unknown>
        );
        defaults[saveKey] = Array.from({ length: minInstances }, () => ({
          ...instanceDefaults,
        }));
      } else {
        module.components.forEach((component) => {
          if (component.fieldId) {
            const v = resolveDefault(component);
            if (v !== undefined) defaults[component.fieldId] = v;
          }
        });
      }
    });
  });

  return defaults;
}

// Full row unless the form asks otherwise, so nothing reflows unasked. One per row when narrow.
const FIELD_WIDTH_SPAN = {
  full: 'col-span-6',
  half: 'col-span-6 sm:col-span-3',
  third: 'col-span-6 sm:col-span-2',
} as const;

const fieldWidthSpan = (width?: string): string =>
  FIELD_WIDTH_SPAN[width as keyof typeof FIELD_WIDTH_SPAN] ??
  FIELD_WIDTH_SPAN.full;

// =============================================================================
// Component Registry
// =============================================================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const componentRegistry: Record<string, React.FC<any>> = {
  Alert: AlertComponent,
  Input: InputComponent,
  TextArea: TextAreaComponent,
  RadioGroup: RadioGroupComponent,
  Select: SelectComponent,
  FileUpload: FileUploadComponent,
  MultiFileUpload: MultiFileUploadComponent,
  PhoneInput: PhoneInputComponent,
  AddressInput: AddressInputComponent,
  PasswordInput: PasswordInputComponent,
  NumberInput: NumberInputComponent,
  UrlInput: UrlInputComponent,
  DateInput: DateInputComponent,
  CheckboxGroup: CheckboxGroupComponent,
  FormSummary: FormSummaryComponent,
  NestedForm: NestedFormComponent,
  SubFormDialog,
  CountryInput: CountryInputComponent,
  // Commenda OS components
  EntitySelect: EntitySelectComponent,
  AutoDetectField: AutoDetectFieldComponent,
  GovernmentIdentifier: GovernmentIdentifierComponent,
  BankAccount: BankAccountComponent,
  Location: LocationComponent,
  RegisteredAgent: RegisteredAgentFieldComponent,
  DirectorDocument: DirectorDocumentComponent,
  Document: DocumentComponent,
  EntitySelectOrCreate: EntitySelectOrCreateComponent,
  BusinessEntitySelectOrCreate: BusinessEntitySelectOrCreateComponent,
  DynamicFields: DynamicFieldsComponent,
  KeyPersons: KeyPersonsComponent,
  Ownership: OwnershipComponent,
  IndirectTaxRegistration: IndirectTaxRegistrationComponent,
};

// =============================================================================
// Storage key for form data persistence
// =============================================================================

const FORM_DATA_STORAGE_KEY_PREFIX = 'form-builder-data-';

// =============================================================================
// File transformation helpers — replace fileUrl with fileId before submission
// =============================================================================

/** Type guard: returns true if `value` looks like a file object with actual file data.
 *  Requires a truthy fileName AND either a valid fileUrl string or numeric fileId/id. */
function isFileValidObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  if (!obj.fileName) return false;
  return (
    (typeof obj.fileUrl === 'string' && obj.fileUrl !== '') ||
    typeof obj.fileId === 'number' ||
    typeof obj.id === 'number'
  );
}

/** Recursively collect all fileId values from form data (including nested/arrays). */
function collectFileIdsFromFormData(data: unknown): number[] {
  const ids: number[] = [];
  if (data === null || data === undefined) return ids;
  if (
    typeof data === 'object' &&
    !Array.isArray(data) &&
    'fileId' in data &&
    typeof (data as Record<string, unknown>).fileId === 'number'
  ) {
    ids.push((data as Record<string, unknown>).fileId as number);
  }
  if (typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    for (const value of Object.values(obj)) {
      ids.push(...collectFileIdsFromFormData(value));
    }
  }
  return ids;
}

/**
 * Normalizes a single file entry so it always carries a stable `fileId`.
 * Handles three shapes: already-persisted (has `fileId`), legacy (has numeric `id`),
 * and raw upload (has only `fileUrl` — persists via API to obtain a `fileId`).
 * Transient URL fields (`fileUrl`, `signedUrl`) are stripped from the result.
 */
async function normalizeFormFileEntry(
  file: Record<string, unknown>,
  corporationId: number,
  serviceRequestId: string | undefined,
  shareToken: string | undefined
): Promise<Record<string, unknown>> {
  // Already has fileId — strip fileUrl / signedUrl and return
  if (typeof file.fileId === 'number') {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { fileUrl, signedUrl, ...rest } = file;
    return rest;
  }

  // Has a legacy numeric id — promote to fileId
  if (typeof file.id === 'number') {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { fileUrl, signedUrl, id, ...rest } = file;
    return { ...rest, fileId: id };
  }

  // Has only a raw fileUrl — create a DB record to obtain fileId
  if (typeof file.fileUrl === 'string' && file.fileUrl) {
    const result = await createFileForFormDocumentSubmission(
      {
        fileUrl: file.fileUrl,
        fileName: String(file.fileName ?? ''),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        documentType: (file.documentType as any) ?? 'Other',
        corporationId,
        serviceRequestId,
      },
      shareToken
    );

    if (!result?.id) {
      throw new Error(
        `Failed to create file record for "${String(file.fileName ?? 'unknown')}". ` +
          'The file cannot be saved without a stable file ID.'
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { fileUrl, signedUrl, id, ...rest } = file;
    return { ...rest, fileId: result.id };
  }

  console.error(
    '[FORM_SAVE] normalizeFormFileEntry: unresolvable file object:',
    JSON.stringify(file, null, 2)
  );
  throw new Error(
    `File "${String(file.fileName ?? 'unknown')}" has no fileId, id, or fileUrl. ` +
      'Cannot persist a file entry without a resolvable identifier.'
  );
}

/** Returns true if `data` contains at least one file object that still carries a raw `fileUrl` (no `fileId`). */
function formDataContainsRawFileUrls(data: unknown): boolean {
  if (data === null || data === undefined) return false;
  if (typeof data !== 'object') return false;

  if (!Array.isArray(data)) {
    const obj = data as Record<string, unknown>;
    if (
      'fileName' in obj &&
      'fileUrl' in obj &&
      typeof obj.fileUrl === 'string' &&
      !('fileId' in obj) &&
      typeof obj.id !== 'number'
    ) {
      return true;
    }
  }

  const values = Array.isArray(data)
    ? data
    : Object.values(data as Record<string, unknown>);
  return values.some((v) => formDataContainsRawFileUrls(v));
}

/**
 * Recursively walks form data and transforms every file entry via {@link normalizeFormFileEntry}.
 * Supports single-file fields, multi-file arrays, and repeatable module rows with nested files.
 */
async function normalizeFormDataFiles(
  data: Record<string, unknown>,
  corporationId: number,
  serviceRequestId: string | undefined,
  shareToken: string | undefined
): Promise<Record<string, unknown>> {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data)) {
    if (isFileValidObject(value)) {
      console.debug(
        `[FORM_SAVE] normalizeFormDataFiles: processing file field "${key}":`,
        JSON.stringify(value)
      );
      result[key] = await normalizeFormFileEntry(
        value as Record<string, unknown>,
        corporationId,
        serviceRequestId,
        shareToken
      );
    } else if (
      Array.isArray(value) &&
      value.length > 0 &&
      isFileValidObject(value[0])
    ) {
      // Multi-file upload
      console.debug(
        `[FORM_SAVE] normalizeFormDataFiles: processing multi-file field "${key}" (${value.length} files)`
      );
      result[key] = await Promise.all(
        value.map((f) =>
          normalizeFormFileEntry(
            f as Record<string, unknown>,
            corporationId,
            serviceRequestId,
            shareToken
          )
        )
      );
    } else if (
      Array.isArray(value) &&
      value.length > 0 &&
      typeof value[0] === 'object' &&
      value[0] !== null
    ) {
      // Repeatable module rows — recurse into each row
      result[key] = await Promise.all(
        value.map(async (row) => {
          if (typeof row === 'object' && row !== null && !Array.isArray(row)) {
            return normalizeFormDataFiles(
              row as Record<string, unknown>,
              corporationId,
              serviceRequestId,
              shareToken
            );
          }
          return row;
        })
      );
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      // Recurse into nested objects so file fields nested under a wrapper object
      // (not just top-level fields and repeatable array rows) are normalized to a fileId.
      result[key] = await normalizeFormDataFiles(
        value as Record<string, unknown>,
        corporationId,
        serviceRequestId,
        shareToken
      );
    } else {
      result[key] = value;
    }
  }

  return result;
}

// =============================================================================
// Main DSL Form Renderer - exported for use in form builder preview
// =============================================================================

export const DSLFormRenderer = ({
  dsl,
  formTemplateId,
  formTemplateKey,
  existingSubmissionId,
  onSubmitSuccess,
  onSaveSuccess,
  onDraftSaved,
  className,
  hideTitle,
  shouldUseContainer,
  onSave,
  onClose,
  companyId,
  corporationId,
  directorId,
  serviceRequestId,
  serviceRequestTaskId,
  prefillFormData,
  header,
  hideTopHeader = false,
  dslContext,
  isPreview = false,
  autoStartTestRun = false,
  onTestRunComplete,
  hideTestingPanel = false,
}: DSLFormRendererProps) => {
  const { shareToken } = useApiScope();

  const effectiveDslContext = useMemo(() => {
    return {
      ...dslContext,
      companyId: dslContext?.companyId ?? companyId,
      corporationId: dslContext?.corporationId ?? corporationId,
      directorId: dslContext?.directorId ?? directorId,
      serviceRequestId: dslContext?.serviceRequestId ?? serviceRequestId,
    };
  }, [dslContext, companyId, corporationId, directorId, serviceRequestId]);

  // Validate context requirements if defined
  useEffect(() => {
    const requirements = (
      dsl as unknown as {
        form?: { contextRequirements?: { required?: string[] } };
      }
    )?.form?.contextRequirements;
    const requiredContextKeys = requirements?.required ?? [];
    const missingContext = requiredContextKeys.filter(
      (key) => effectiveDslContext[key] === undefined
    );
    if (missingContext.length > 0) {
      console.warn(
        `Form requires context: ${missingContext.join(', ')}. Some Commenda OS features may not work correctly.`
      );
    }
  }, [dsl, effectiveDslContext]);
  // Collect bindings, disabled bindings, and defaults once
  const router = useRouter();
  const bindingsMap = useMemo(() => collectBindings(dsl), [dsl]);
  const disabledBindings = useMemo(() => collectDisabledBindings(dsl), [dsl]);
  const defaultValues = useMemo(
    () =>
      collectDefaultValues(dsl, effectiveDslContext as Record<string, unknown>),
    [dsl, effectiveDslContext]
  );
  const fieldLabelMap = useMemo(() => buildFieldLabelMap(dsl), [dsl]);
  const componentMap = useMemo(() => buildComponentMap(dsl), [dsl]);

  // Scope persisted draft data to the service request + task.
  // This prevents data from the same form template bleeding across entities.
  const storageKey =
    serviceRequestId && serviceRequestTaskId
      ? `${FORM_DATA_STORAGE_KEY_PREFIX}${serviceRequestId}:${serviceRequestTaskId}`
      : null;

  // Track fields the user has edited so we don't overwrite with reactive defaultExpression
  const userTouchedFieldsRef = useRef<Set<string>>(new Set());

  // Initialize form data from localStorage or defaults.
  // When an existing server submission is being loaded, skip localStorage so the
  // server draft is treated as the source of truth.
  const [formData, setFormData] = useState<Record<string, unknown>>(() => {
    if (!existingSubmissionId && typeof window !== 'undefined' && storageKey) {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          // Local draft (unsaved edits) wins over prefill from a prior submission.
          return { ...defaultValues, ...prefillFormData, ...parsed.formData };
        }
      } catch (error) {
        console.warn('Failed to load form data from localStorage:', error);
      }
    }
    // Prefill a fresh form from the latest prior submission (e.g. after a reset).
    return { ...defaultValues, ...prefillFormData };
  });

  // Initialize current step from localStorage (skip when loading a server draft)
  const [currentStep, setCurrentStep] = useState(() => {
    if (!existingSubmissionId && typeof window !== 'undefined' && storageKey) {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          return parsed.currentStep ?? 0;
        }
      } catch {
        // Ignore errors, use default
      }
    }
    return 0;
  });

  // Restore which nested form drawer was open (validated against current DSL)
  const [nestedFormOpen, setNestedFormOpen] = useState<{
    fieldId: string;
    editingIndex: number | null;
  } | null>(() => {
    if (typeof window === 'undefined' || !storageKey) return null;
    try {
      const saved = localStorage.getItem(storageKey);
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      const open = parsed.nestedFormOpen;
      if (!open || typeof open.fieldId !== 'string') return null;
      const validIds = getNestedFormFieldIds(dsl);
      if (!validIds.has(open.fieldId)) return null;
      const editingIndex =
        open.editingIndex === null || open.editingIndex === undefined
          ? null
          : Number(open.editingIndex);
      return {
        fieldId: open.fieldId,
        editingIndex: Number.isNaN(editingIndex) ? null : editingIndex,
      };
    } catch {
      return null;
    }
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Track which modules are expanded (by module ID)
  const [expandedModules, setExpandedModules] = useState<Set<string>>(() => {
    const stepData = dsl?.steps?.[0];
    return getDefaultExpandedModulesForStep(stepData);
  });

  // Track focused module for auto-expansion
  const lastFocusedModuleRef = useRef<string | null>(null);

  // Submission tracking state
  const [submissionId, setSubmissionId] = useState<string | null>(
    existingSubmissionId ?? null
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Re-entrancy guard for rapid double-clicks (state lags a render tick).
  const isSubmittingRef = useRef(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [, setSaveError] = useState<string | null>(null);

  // Track which fields are currently uploading files
  const [uploadingFields, setUploadingFields] = useState<Set<string>>(
    () => new Set()
  );
  const setFieldUploading = useCallback(
    (fieldId: string, isUploading: boolean) => {
      setUploadingFields((prev) => {
        const next = new Set(prev);
        if (isUploading) {
          next.add(fieldId);
        } else {
          next.delete(fieldId);
        }
        return next;
      });
    },
    []
  );
  const [notApplicableFields, setNotApplicableFields] = useState<Set<string>>(
    () => new Set()
  );
  const setFieldNotApplicable = useCallback(
    (fieldId: string, notApplicable: boolean) => {
      // An error left from before the field stopped applying would still block the step.
      if (notApplicable) {
        setErrors((prev) => {
          if (!(fieldId in prev)) return prev;
          const next = { ...prev };
          delete next[fieldId];
          return next;
        });
      }
      setNotApplicableFields((prev) => {
        if (prev.has(fieldId) === notApplicable) return prev;
        const next = new Set(prev);
        if (notApplicable) {
          next.add(fieldId);
        } else {
          next.delete(fieldId);
        }
        return next;
      });
    },
    []
  );

  // Fetch existing submission data if existingSubmissionId is provided
  const {
    submission: existingSubmission,
    error: existingSubmissionError,
    isLoading: isLoadingSubmission,
    mutate: retryExistingSubmission,
  } = useFormSubmission(existingSubmissionId);

  // Populate form data from existing submission only on first successful load
  // for a given submissionId. SWR revalidates on focus/reconnect/intervals; if we
  // re-merged on every revalidation, the (potentially stale) server formResponse
  // would clobber local edits made since — extra repeatable instances, just-
  // uploaded files, etc. Reset the guard if the submission id changes so a
  // different draft hydrates correctly.
  const hydratedSubmissionIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      existingSubmissionId !== hydratedSubmissionIdRef.current &&
      existingSubmissionId == null
    ) {
      hydratedSubmissionIdRef.current = null;
    }
  }, [existingSubmissionId]);
  useEffect(() => {
    if (!existingSubmission?.formResponse) return;
    if (hydratedSubmissionIdRef.current === existingSubmissionId) return;
    hydratedSubmissionIdRef.current = existingSubmissionId ?? null;
    setFormData((prev) => ({
      ...defaultValues,
      ...prev,
      ...(existingSubmission.formResponse as Record<string, unknown>),
    }));
  }, [existingSubmission, defaultValues, existingSubmissionId]);

  // Stable ref for prefill callback so useCommendaOSPrefill effect deps don't change every
  // render and trigger infinite /read requests (e.g. after final submit re-renders).
  const onPrefillCompleteRef =
    useRef<(data: Record<string, unknown>) => void>();
  onPrefillCompleteRef.current = (prefillData: Record<string, unknown>) => {
    setFormData((prev) => {
      const next = { ...prev };
      Object.entries(prefillData).forEach(([key, val]) => {
        if (!userTouchedFieldsRef.current.has(key)) {
          next[key] = val;
        }
      });
      return next;
    });
  };
  const stableOnPrefillComplete = useCallback(
    (data: Record<string, unknown>) => onPrefillCompleteRef.current?.(data),
    []
  );

  // Commenda OS DSL prefill integration
  // Prefills form fields that have commenda-os data source configured
  const { isLoading: isPrefilling } = useCommendaOSPrefill({
    dsl: dsl as FormDSL,
    dslContext: effectiveDslContext as DSLContext,
    formData, // Pass current form data to avoid overwriting existing values
    onPrefillComplete: stableOnPrefillComplete,
  });

  // Commenda OS DSL submit integration
  // Handles writing form data back to Commenda OS entities
  const { submit: submitToCommendaOS, isSubmitting: isSubmittingToCommendaOS } =
    useCommendaOSSubmit({
      dsl: dsl as FormDSL,
      dslContext: effectiveDslContext as DSLContext,
      formData,
    });

  // Save form data to localStorage whenever it changes.
  // Stop writing once a backend submission exists (submissionId set) — at that
  // point Temporal is the source of truth and localStorage is no longer needed.
  useEffect(() => {
    if (typeof window === 'undefined' || !storageKey || submissionId) return;

    const dataToSave = {
      formData,
      currentStep,
      nestedFormOpen,
      savedAt: new Date().toISOString(),
    };

    try {
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
    } catch (error) {
      console.warn('Failed to save form data to localStorage:', error);
    }
  }, [formData, currentStep, nestedFormOpen, storageKey, submissionId]);

  // Save on page unload (beforeunload event)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (typeof window === 'undefined' || !storageKey || submissionId) return;

      const dataToSave = {
        formData,
        currentStep,
        nestedFormOpen,
        savedAt: new Date().toISOString(),
      };

      try {
        localStorage.setItem(storageKey, JSON.stringify(dataToSave));
      } catch (error) {
        console.warn('Failed to save form data on unload:', error);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [formData, currentStep, nestedFormOpen, storageKey, submissionId]);

  const isFieldVisible = useCallback(
    (visibility?: ComponentVisibility): boolean => {
      if (!visibility) return true;

      const { condition } = visibility;

      if (
        condition.field &&
        condition.operator &&
        (condition.value !== undefined || condition.operator === 'isNotEmpty')
      ) {
        // Resolve `context:<key>` from dslContext, then dotted paths
        // (e.g. `directors.0.email`), then plain field ids.
        const fieldValue = condition.field.startsWith('context:')
          ? effectiveDslContext?.[condition.field.slice('context:'.length)]
          : condition.field.includes('.')
            ? getFormValueByPath(formData, condition.field)
            : formData[condition.field];
        const fieldComponent = componentMap[condition.field];

        // Handle CheckboxGroup fields: values are stored as Record<string, boolean>
        if (
          fieldComponent?.component === 'CheckboxGroup' &&
          typeof fieldValue === 'object' &&
          fieldValue !== null &&
          !Array.isArray(fieldValue)
        ) {
          const checkboxValue = fieldValue as Record<string, boolean>;
          const optionKey = String(condition.value);

          switch (condition.operator) {
            case 'equals':
              return checkboxValue[optionKey] === true;
            case 'notEquals':
              return checkboxValue[optionKey] !== true;
            case 'contains':
              // Check if any checked option matches the condition value
              return Object.entries(checkboxValue).some(
                ([key, checked]) => checked === true && key === optionKey
              );
            case 'isNotEmpty':
              return Object.values(checkboxValue).some(
                (checked) => checked === true
              );
            default:
              return true;
          }
        }

        // Default behavior for non-checkbox fields
        switch (condition.operator) {
          case 'equals':
            return fieldValue === condition.value;
          case 'notEquals':
            return fieldValue !== condition.value;
          case 'greaterThan':
            return Number(fieldValue) > Number(condition.value);
          case 'lessThan':
            return Number(fieldValue) < Number(condition.value);
          case 'contains':
            if (Array.isArray(fieldValue)) {
              return fieldValue.some((item) => item === condition.value);
            }
            return String(fieldValue ?? '').includes(String(condition.value));
          case 'isNotEmpty':
            return Array.isArray(fieldValue)
              ? fieldValue.length > 0
              : fieldValue !== null &&
                  fieldValue !== undefined &&
                  fieldValue !== '';
          default:
            return true;
        }
      }

      if (condition.and) {
        return condition.and.every((cond) =>
          isFieldVisible({ condition: cond })
        );
      }

      if (condition.or) {
        return condition.or.some((cond) => isFieldVisible({ condition: cond }));
      }

      return true;
    },
    [formData, componentMap, effectiveDslContext]
  );

  // Reset expanded modules only when the user navigates to a different step.
  // We deliberately exclude isFieldVisible from deps so that form data changes
  // (e.g. clicking a radio button) don't collapse all accordions.
  const prevStepRef = useRef(currentStep);
  const hasInitializedExpandedModulesRef = useRef(false);
  useEffect(() => {
    if (!hasInitializedExpandedModulesRef.current) {
      hasInitializedExpandedModulesRef.current = true;
    } else if (prevStepRef.current === currentStep) {
      return;
    }
    prevStepRef.current = currentStep;

    const steps = (dsl?.steps ?? []).filter((step) =>
      isFieldVisible(step.visibility as ComponentVisibility)
    );
    const stepData = steps[currentStep];
    setExpandedModules(
      getDefaultExpandedModulesForStep(stepData, (module) =>
        isFieldVisible(module.visibility as ComponentVisibility)
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, dsl?.steps]);

  // Handle module expand/collapse
  const handleModuleExpandChange = useCallback(
    (moduleId: string, expanded: boolean) => {
      setExpandedModules((prev) => {
        const next = new Set(prev);
        if (expanded) {
          next.add(moduleId);
        } else {
          next.delete(moduleId);
        }
        return next;
      });
    },
    []
  );

  // Handle expanding next module when last field of previous module is focused
  const handleFieldFocus = useCallback(
    (moduleId: string, isLastFieldInModule: boolean) => {
      if (!isLastFieldInModule) return;

      lastFocusedModuleRef.current = moduleId;

      // Find the next module and expand it (use visible steps for current step data)
      const steps = (dsl?.steps ?? []).filter((step) =>
        isFieldVisible(step.visibility as ComponentVisibility)
      );
      const stepData = steps[currentStep];
      if (!stepData?.modules) return;

      const moduleIndex = stepData.modules.findIndex((m) => m.id === moduleId);
      if (moduleIndex === -1 || moduleIndex === stepData.modules.length - 1)
        return;

      const nextModule = stepData.modules[moduleIndex + 1];
      if (nextModule) {
        setExpandedModules((prev) => {
          const next = new Set(prev);
          next.add(nextModule.id);
          return next;
        });
      }
    },
    [currentStep, dsl?.steps, isFieldVisible]
  );

  // Check if a field is disabled due to binding
  const isFieldDisabled = useCallback(
    (fieldName?: string): boolean => {
      if (!fieldName) return false;
      return disabledBindings.has(fieldName);
    },
    [disabledBindings]
  );

  const updateField = useCallback(
    (fieldName: string, value: unknown) => {
      userTouchedFieldsRef.current.add(fieldName);
      setFormData((prev) => {
        const updated = { ...prev, [fieldName]: value };

        // Apply bindings - if this field is a source, update all bound target fields
        const boundTargets = bindingsMap.get(fieldName);
        if (boundTargets) {
          boundTargets.forEach((targetField) => {
            updated[targetField] = value;
          });
        }

        return updated;
      });
    },
    [bindingsMap]
  );

  // Reactive $field: defaultExpression — when source field changes, update dependent field if not user-touched
  const defaultExpressionDependents = useMemo(() => {
    const list: { targetFieldId: string; sourceFieldId: string }[] = [];
    Object.entries(componentMap).forEach(([fieldId, component]) => {
      const sourceId = component.defaultExpression
        ? fieldTokenId(component.defaultExpression)
        : undefined;
      if (sourceId)
        list.push({ targetFieldId: fieldId, sourceFieldId: sourceId });
    });
    return list;
  }, [componentMap]);

  useEffect(() => {
    if (defaultExpressionDependents.length === 0) return;
    setFormData((prev) => {
      let next: Record<string, unknown> | null = null;
      for (const {
        targetFieldId,
        sourceFieldId,
      } of defaultExpressionDependents) {
        if (userTouchedFieldsRef.current.has(targetFieldId)) continue;
        const v = prev[sourceFieldId];
        if (v === undefined) continue;
        const targetValue = prev[targetFieldId];
        if (targetValue != null && targetValue !== '') continue;
        if (!next) next = { ...prev };
        next[targetFieldId] = v;
      }
      return next ?? prev;
    });
  }, [defaultExpressionDependents]);

  const validateField = useCallback(
    (
      fieldName: string,
      value: unknown,
      rules?: ValidationRules
    ): string | null => {
      if (!rules) return null;

      const shared = fieldValidationError(value, rules, {
        isFieldVisible,
        resolveField: (field) =>
          field.includes('.') ? getFormValueByPath(formData, field) : formData[field],
      });
      if (shared) {
        setErrors((prev) => ({ ...prev, [fieldName]: shared }));
        return shared;
      }

      // Unique values within repeatable module (step/submit validation)
      if (isRuleActive(rules.uniqueInRepeatable, isFieldVisible)) {
        const match = fieldName.match(/^(.+)\.(\d+)\.(.+)$/);
        if (match) {
          const [, saveKey, , fieldId] = match;
          const instances = formData[saveKey] as
            | Record<string, unknown>[]
            | undefined;
          if (Array.isArray(instances)) {
            const values = instances.map(
              (inst) => (inst ?? {})[fieldId] as unknown
            );
            const nonEmpty = values.filter(
              (v) => v !== '' && v !== null && v !== undefined
            );
            const normalized = nonEmpty.map((v) => String(v));
            const currentNorm =
              value !== '' && value !== null && value !== undefined
                ? String(value)
                : null;
            if (
              currentNorm &&
              normalized.filter((v) => v === currentNorm).length > 1
            ) {
              const message =
                rules.uniqueInRepeatable.message ||
                'This value must be unique across all entries';
              setErrors((prev) => ({ ...prev, [fieldName]: message }));
              return message;
            }
          }
        }
      }

      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
      return null;
    },
    [formData, isFieldVisible]
  );

  const visibleSteps = useMemo(
    () =>
      (dsl?.steps ?? []).filter((step) =>
        isFieldVisible(step.visibility as ComponentVisibility)
      ),
    [dsl?.steps, isFieldVisible]
  );

  // A collapsed module is unmounted, not hidden, so a failed validation inside
  // one renders no error and the querySelector below finds nothing — the button
  // just looks dead. Open the offending section first, then scroll to the field.
  const revealFieldWithError = useCallback(
    (step: FormStep | undefined, fieldId: string | undefined) => {
      const moduleId = findModuleIdForField(step, fieldId);
      if (moduleId) {
        setExpandedModules((prev) => new Set(prev).add(moduleId));
      }
      if (!fieldId || typeof document === 'undefined') return;
      // Let the newly expanded module mount before looking for the field.
      setTimeout(() => {
        document
          .querySelector(`[data-field-id="${fieldId}"]`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    },
    []
  );

  const validateCurrentStep = useCallback((): {
    isValid: boolean;
    firstErrorField?: string;
  } => {
    const step = visibleSteps[currentStep];
    if (!step) return { isValid: true };

    let hasErrors = false;
    let firstErrorField: string | undefined;

    // Iterate through all modules in the current step
    step.modules?.forEach((module) => {
      if (
        module.visibility &&
        !isFieldVisible(module.visibility as ComponentVisibility)
      ) {
        return;
      }
      const repeatable = module.repeatable as
        | { enabled: boolean; min: number }
        | undefined;
      const saveKey = module.saveKey || module.id;

      const validateComponent = (
        component: FormComponent,
        fieldId: string,
        value: unknown
      ) => {
        const validationRules = component.validation?.rules;
        if (!validationRules) return;
        // Read-only fields are populated from Commenda OS at runtime; in preview
        // there's no entity behind them, so an unfillable required field would
        // dead-end the Next button. Drop only the required check in that case,
        // and for a field its component found nothing to ask.
        const rules =
          (isPreview && isComponentReadOnly(component)) ||
          notApplicableFields.has(fieldId)
            ? { ...validationRules, required: undefined }
            : validationRules;
        const error = validateField(fieldId, value, rules);
        if (error && !firstErrorField) {
          hasErrors = true;
          firstErrorField = fieldId;
        }
      };

      if (repeatable?.enabled) {
        const instances =
          (formData[saveKey] as Record<string, unknown>[]) ?? [];
        const arr = Array.isArray(instances) ? instances : [];
        arr.forEach((instanceData, instanceIndex) => {
          const instancePrefix = `${saveKey}.${instanceIndex}.`;
          module.components?.forEach((component) => {
            // Resolve visibility against this instance the same way the renderer
            // does, so a field the user can see is never wrongly judged hidden
            // and skipped (top-level triggers must resolve at the top level).
            if (
              !isInstanceFieldVisible(
                component.visibility,
                instanceData ?? {},
                isFieldVisible,
                componentMap
              )
            )
              return;
            if (!component.fieldId) return;
            if (component.component === 'FormSummary') return;

            const fieldId = `${saveKey}.${instanceIndex}.${component.fieldId}`;
            const value = (instanceData ?? {})[component.fieldId];

            // NestedForm min/max validation (repeatable module)
            if (component.component === 'NestedForm') {
              const entries = Array.isArray(value) ? value : [];
              const count = entries.length;
              const minProp = component.props?.min;
              const maxProp = component.props?.max;
              const minCount =
                minProp != null && minProp !== '' ? Number(minProp) : undefined;
              const maxCount =
                maxProp != null && maxProp !== '' ? Number(maxProp) : undefined;
              if (minCount != null && minCount > 0 && count < minCount) {
                const msg = `Please add at least ${minCount} entr${minCount === 1 ? 'y' : 'ies'}`;
                validateField(fieldId, null, {
                  required: { enabled: true, message: msg },
                });
                hasErrors = true;
                if (!firstErrorField) firstErrorField = fieldId;
                return;
              }
              if (maxCount != null && count > maxCount) {
                const msg = `Please add at most ${maxCount} entr${maxCount === 1 ? 'y' : 'ies'}`;
                validateField(fieldId, value, {
                  maxLength: { value: maxCount, message: msg },
                });
                hasErrors = true;
                if (!firstErrorField) firstErrorField = fieldId;
                return;
              }
            }

            // Rewrite sibling refs in rules (notSameAs.field, rule.when.field)
            // before validating, so cross-field rules resolve to this instance.
            const scopedRules = rewriteRulesForInstance(
              component.validation?.rules,
              instancePrefix
            );
            if (scopedRules) {
              const error = validateField(fieldId, value, scopedRules);
              if (error && !firstErrorField) {
                hasErrors = true;
                firstErrorField = fieldId;
              }
            }
          });
        });
      } else {
        module.components?.forEach((component) => {
          if (!isFieldVisible(component.visibility)) return;
          if (!component.fieldId) return;
          if (component.component === 'FormSummary') return;

          // NestedForm min/max validation (non-repeatable module)
          if (component.component === 'NestedForm') {
            const entries = formData[component.fieldId];
            const count = Array.isArray(entries) ? entries.length : 0;
            const minProp = component.props?.min;
            const maxProp = component.props?.max;
            const minCount =
              minProp != null && minProp !== '' ? Number(minProp) : undefined;
            const maxCount =
              maxProp != null && maxProp !== '' ? Number(maxProp) : undefined;
            if (minCount != null && minCount > 0 && count < minCount) {
              const msg = `Please add at least ${minCount} entr${minCount === 1 ? 'y' : 'ies'}`;
              validateField(component.fieldId, null, {
                required: { enabled: true, message: msg },
              });
              hasErrors = true;
              if (!firstErrorField) firstErrorField = component.fieldId;
              return;
            }
            if (maxCount != null && count > maxCount) {
              const msg = `Please add at most ${maxCount} entr${maxCount === 1 ? 'y' : 'ies'}`;
              validateField(component.fieldId, entries, {
                maxLength: { value: maxCount, message: msg },
              });
              hasErrors = true;
              if (!firstErrorField) firstErrorField = component.fieldId;
              return;
            }
            return;
          }

          const value = formData[component.fieldId];
          validateComponent(component, component.fieldId, value);
        });
      }
    });

    if (!hasErrors) {
      const visibleFieldIds = new Set<string>();
      step.modules?.forEach((module) => {
        if (
          module.visibility &&
          !isFieldVisible(module.visibility as ComponentVisibility)
        )
          return;
        module.components?.forEach((component) => {
          if (component.fieldId && isFieldVisible(component.visibility)) {
            visibleFieldIds.add(component.fieldId);
          }
        });
      });
      const blockingFieldId = Object.keys(errors).find(
        (id) => errors[id] && visibleFieldIds.has(id)
      );
      if (blockingFieldId) {
        hasErrors = true;
        firstErrorField = blockingFieldId;
      }
    }

    return { isValid: !hasErrors, firstErrorField };
  }, [
    currentStep,
    visibleSteps,
    formData,
    isFieldVisible,
    validateField,
    errors,
    componentMap,
    isPreview,
    notApplicableFields,
  ]);

  const validateAllSteps = useCallback((): {
    isValid: boolean;
    firstErrorField?: string;
    errorStep?: number;
  } => {
    if (!visibleSteps.length) return { isValid: true };

    // Validate all visible steps
    for (let stepIndex = 0; stepIndex < visibleSteps.length; stepIndex++) {
      const step = visibleSteps[stepIndex];
      if (!step) continue;

      let hasErrors = false;
      let firstErrorField: string | undefined;

      step.modules?.forEach((module) => {
        if (
          module.visibility &&
          !isFieldVisible(module.visibility as ComponentVisibility)
        ) {
          return;
        }
        const repeatable = module.repeatable as
          | { enabled: boolean; min: number }
          | undefined;
        const saveKey = module.saveKey || module.id;

        const validateComponent = (
          component: FormComponent,
          fieldId: string,
          value: unknown
        ) => {
          const validationRules = component.validation?.rules;
          if (!validationRules) return;
          // Read-only fields are populated from Commenda OS at runtime; in preview
          // there's no entity behind them, so an unfillable required field would
          // dead-end the Next button. Drop only the required check in that case,
          // and for a field its component found nothing to ask.
          const rules =
            (isPreview && isComponentReadOnly(component)) ||
            notApplicableFields.has(fieldId)
              ? { ...validationRules, required: undefined }
              : validationRules;
          const error = validateField(fieldId, value, rules);
          if (error && !firstErrorField) {
            hasErrors = true;
            firstErrorField = fieldId;
          }
        };

        if (repeatable?.enabled) {
          const instances =
            (formData[saveKey] as Record<string, unknown>[]) ?? [];
          const arr = Array.isArray(instances) ? instances : [];
          arr.forEach((instanceData, instanceIndex) => {
            const instancePrefix = `${saveKey}.${instanceIndex}.`;
            module.components?.forEach((component) => {
              // Resolve visibility against this instance, matching the renderer,
              // so visible required fields are never skipped on submit.
              if (
                !isInstanceFieldVisible(
                  component.visibility,
                  instanceData ?? {},
                  isFieldVisible,
                  componentMap
                )
              )
                return;
              if (!component.fieldId) return;
              if (component.component === 'FormSummary') return;

              const fieldId = `${saveKey}.${instanceIndex}.${component.fieldId}`;
              const value = (instanceData ?? {})[component.fieldId];

              // NestedForm min/max validation (repeatable module)
              if (component.component === 'NestedForm') {
                const entries = Array.isArray(value) ? value : [];
                const count = entries.length;
                const minProp = component.props?.min;
                const maxProp = component.props?.max;
                const minCount =
                  minProp != null && minProp !== ''
                    ? Number(minProp)
                    : undefined;
                const maxCount =
                  maxProp != null && maxProp !== ''
                    ? Number(maxProp)
                    : undefined;
                if (minCount != null && minCount > 0 && count < minCount) {
                  const msg = `Please add at least ${minCount} entr${minCount === 1 ? 'y' : 'ies'}`;
                  validateField(fieldId, null, {
                    required: { enabled: true, message: msg },
                  });
                  hasErrors = true;
                  if (!firstErrorField) firstErrorField = fieldId;
                  return;
                }
                if (maxCount != null && count > maxCount) {
                  const msg = `Please add at most ${maxCount} entr${maxCount === 1 ? 'y' : 'ies'}`;
                  validateField(fieldId, value, {
                    maxLength: { value: maxCount, message: msg },
                  });
                  hasErrors = true;
                  if (!firstErrorField) firstErrorField = fieldId;
                  return;
                }
              }

              // Rewrite sibling refs in rules before validating.
              const scopedRules = rewriteRulesForInstance(
                component.validation?.rules,
                instancePrefix
              );
              if (scopedRules) {
                const error = validateField(fieldId, value, scopedRules);
                if (error && !firstErrorField) {
                  hasErrors = true;
                  firstErrorField = fieldId;
                }
              }
            });
          });
        } else {
          module.components?.forEach((component) => {
            if (!isFieldVisible(component.visibility)) return;
            if (!component.fieldId) return;
            if (component.component === 'FormSummary') return;
            if (isModuleLockingComponent(component.component)) return;

            // NestedForm min/max validation (non-repeatable module)
            if (component.component === 'NestedForm') {
              const entries = formData[component.fieldId];
              const count = Array.isArray(entries) ? entries.length : 0;
              const minProp = component.props?.min;
              const maxProp = component.props?.max;
              const minCount =
                minProp != null && minProp !== '' ? Number(minProp) : undefined;
              const maxCount =
                maxProp != null && maxProp !== '' ? Number(maxProp) : undefined;
              if (minCount != null && minCount > 0 && count < minCount) {
                const msg = `Please add at least ${minCount} entr${minCount === 1 ? 'y' : 'ies'}`;
                validateField(component.fieldId, null, {
                  required: { enabled: true, message: msg },
                });
                hasErrors = true;
                if (!firstErrorField) firstErrorField = component.fieldId;
                return;
              }
              if (maxCount != null && count > maxCount) {
                const msg = `Please add at most ${maxCount} entr${maxCount === 1 ? 'y' : 'ies'}`;
                validateField(component.fieldId, entries, {
                  maxLength: { value: maxCount, message: msg },
                });
                hasErrors = true;
                if (!firstErrorField) firstErrorField = component.fieldId;
                return;
              }
              return;
            }

            const value = formData[component.fieldId];
            validateComponent(component, component.fieldId, value);
          });
        }
      });

      if (hasErrors) {
        return { isValid: false, firstErrorField, errorStep: stepIndex };
      }
    }

    // Also check for any existing errors in the errors state
    // This catches errors set by components like GovernmentIdentifier that validate with runtime schema data
    const existingErrors = Object.entries(errors).filter(([, error]) => error);
    if (existingErrors.length > 0) {
      const [firstErrorFieldId] = existingErrors[0] ?? [];
      // Find which visible step contains this field
      for (let stepIndex = 0; stepIndex < visibleSteps.length; stepIndex++) {
        const step = visibleSteps[stepIndex];
        if (!step) continue;

        for (const module of step.modules ?? []) {
          for (const component of module.components ?? []) {
            if (component.fieldId === firstErrorFieldId) {
              return {
                isValid: false,
                firstErrorField: firstErrorFieldId,
                errorStep: stepIndex,
              };
            }
          }
        }
      }
      // If we couldn't find the step, just return invalid
      return { isValid: false, firstErrorField: firstErrorFieldId };
    }

    return { isValid: true };
  }, [
    visibleSteps,
    formData,
    isFieldVisible,
    validateField,
    errors,
    componentMap,
    isPreview,
    notApplicableFields,
  ]);

  // Composite nodes (e.g. Ownership) register a callback here that runs on
  // submit, after the standard COS field writes — for values mapping to many
  // DSL writes. Keyed by fieldId; latest registration wins.
  const submitHandlersRef = useRef<
    Map<string, () => Promise<Record<string, unknown> | void>>
  >(new Map());
  const registerSubmitHandler = useCallback(
    (id: string, handler: () => Promise<Record<string, unknown> | void>) => {
      submitHandlersRef.current.set(id, handler);
    },
    []
  );

  // Preview-only self-fill handlers (see FormContext.registerPreviewFillHandler).
  const previewFillHandlersRef = useRef<Map<string, PreviewFillHandler>>(
    new Map()
  );
  const registerPreviewFillHandler = useCallback(
    (id: string, handler: PreviewFillHandler | null) => {
      if (handler) previewFillHandlersRef.current.set(id, handler);
      else previewFillHandlersRef.current.delete(id);
    },
    []
  );
  const getPreviewFillHandler = useCallback(
    (id: string) => previewFillHandlersRef.current.get(id),
    []
  );

  const contextValue: FormContextType = useMemo(
    () => ({
      formData,
      setFormData,
      updateField,
      currentStep,
      setCurrentStep,
      errors,
      setErrors,
      validateField,
      isFieldVisible,
      isFieldDisabled,
      dslContext: effectiveDslContext as DSLContext,
      formTemplateId,
      formTemplateKey,
      nestedFormOpen,
      setNestedFormOpen,
      uploadingFields,
      setFieldUploading,
      componentMap,
      isPreview,
      registerSubmitHandler,
      setFieldNotApplicable,
      registerPreviewFillHandler,
      hasAttemptedSubmit,
    }),
    [
      formData,
      updateField,
      currentStep,
      errors,
      validateField,
      isFieldVisible,
      isFieldDisabled,
      effectiveDslContext,
      formTemplateId,
      formTemplateKey,
      nestedFormOpen,
      setNestedFormOpen,
      uploadingFields,
      isPreview,
      setFieldUploading,
      componentMap,
      registerSubmitHandler,
      setFieldNotApplicable,
      registerPreviewFillHandler,
      hasAttemptedSubmit,
    ]
  );

  const currentStepData = visibleSteps[currentStep];

  // Clamp currentStep when visible steps shrink (e.g. trigger answer hides steps)
  useEffect(() => {
    if (visibleSteps.length > 0 && currentStep >= visibleSteps.length) {
      setCurrentStep(Math.max(0, visibleSteps.length - 1));
    }
  }, [visibleSteps.length, currentStep]);

  // Merges fileId values from the normalized submission payload back into the original
  // formData object, preserving fileUrl/signedUrl for UI display/preview while ensuring
  // subsequent saves see the fileId and skip creating duplicate DB records.
  function mergeNormalizedFileIds(
    original: Record<string, unknown>,
    normalized: Record<string, unknown>
  ): Record<string, unknown> {
    const result = { ...original };
    for (const [key, normValue] of Object.entries(normalized)) {
      const origValue = original[key];
      if (
        normValue &&
        typeof normValue === 'object' &&
        !Array.isArray(normValue) &&
        'fileId' in normValue &&
        origValue &&
        typeof origValue === 'object' &&
        !Array.isArray(origValue)
      ) {
        result[key] = {
          ...(origValue as Record<string, unknown>),
          fileId: (normValue as Record<string, unknown>).fileId,
        };
      } else if (
        Array.isArray(normValue) &&
        Array.isArray(origValue) &&
        normValue.length === origValue.length &&
        normValue[0] &&
        typeof normValue[0] === 'object' &&
        'fileId' in normValue[0]
      ) {
        // Multi-file array
        result[key] = origValue.map((origFile, i) => ({
          ...(origFile as Record<string, unknown>),
          fileId: (normValue[i] as Record<string, unknown>).fileId,
        }));
      } else if (
        Array.isArray(normValue) &&
        Array.isArray(origValue) &&
        normValue.length === origValue.length &&
        normValue.length > 0 &&
        normValue[0] &&
        typeof normValue[0] === 'object' &&
        !('fileId' in (normValue[0] as object))
      ) {
        // Repeatable module rows — recurse into each row so fileIds inside
        // nested file fields get written back to state
        result[key] = origValue.map((origRow, i) => {
          const normRow = normValue[i];
          if (
            typeof origRow === 'object' &&
            origRow !== null &&
            !Array.isArray(origRow) &&
            typeof normRow === 'object' &&
            normRow !== null &&
            !Array.isArray(normRow)
          ) {
            return mergeNormalizedFileIds(
              origRow as Record<string, unknown>,
              normRow as Record<string, unknown>
            );
          }
          return origRow;
        });
      }
    }
    return result;
  }

  // Helper function to save form data to the backend
  const saveToBackend = useCallback(
    async (
      status: FormsSubmissionStatus,
      commendaOSFailures?: ICommendaOSWriteFailure[],
      formDataOverrides?: Record<string, unknown>
    ): Promise<string | null> => {
      // In preview mode, never write to the backend.
      if (isPreview) return null;

      const effectiveFormData = formDataOverrides
        ? { ...formData, ...formDataOverrides }
        : formData;

      setIsSaving(true);
      setSaveError(null);

      // // 🔑 transform keys just for the backend payload
      // const backendFormData = mapFormDataForBackend(
      //   formData as Record<string, unknown>,
      //   fieldLabelMap
      // );

      try {
        if (!corporationId && formDataContainsRawFileUrls(effectiveFormData)) {
          const msg =
            'Cannot save: files require a corporation context to be persisted.';
          console.error('[FORM_SAVE]', msg);
          setSaveError(msg);
          return null;
        }

        const submissionFormData = corporationId
          ? await normalizeFormDataFiles(
              effectiveFormData,
              corporationId,
              serviceRequestId,
              shareToken
            )
          : effectiveFormData;

        // Write fileIds back into state so subsequent saves (auto-save, save-and-close,
        // or a repeated submit) don't re-process the same raw fileUrl and create duplicate
        // File DB records. fileUrl/signedUrl are intentionally preserved for UI display.
        if (corporationId && submissionFormData !== effectiveFormData) {
          setFormData((prev) =>
            mergeNormalizedFileIds(
              prev,
              submissionFormData as Record<string, unknown>
            )
          );
        }

        if (submissionId) {
          const result = await updateFormSubmission(
            submissionId,
            {
              formData: submissionFormData,
              status,
              ...(commendaOSFailures?.length ? { commendaOSFailures } : {}),
            },
            shareToken
          );

          if (!result.success) {
            setSaveError('Failed to save form data');
            return null;
          }
          return submissionId;
        }

        const result = await createFormSubmission(
          {
            formTemplateId,
            formTemplateKey,
            formData: submissionFormData,
            status,
            companyId,
            serviceRequestId,
            ...(commendaOSFailures?.length ? { commendaOSFailures } : {}),
            // Record the originating task so the submission carries its own history
            // linkage (the task layer only tracks the latest submission pointer).
            ...(serviceRequestTaskId != null
              ? {
                  sourceEntityType:
                    FormSubmissionSourceType.SERVICE_REQUEST_TASK,
                  sourceEntityId: String(serviceRequestTaskId),
                }
              : {}),
          },
          shareToken
        );

        if (!result.success || !result.data) {
          console.error('[FORM_SAVE] createFormSubmission failed:', result);
          setSaveError('Failed to save form data');
          return null;
        }

        const newSubmissionId = result.data.formSubmission.id;
        setSubmissionId(newSubmissionId);
        if (storageKey) {
          try {
            localStorage.removeItem(storageKey);
          } catch {
            /* ignore */
          }
        }

        return newSubmissionId;
      } catch (error) {
        console.error('Error saving form submission:', error);
        setSaveError('An unexpected error occurred while saving');
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [
      submissionId,
      formData,
      formTemplateId,
      formTemplateKey,
      fieldLabelMap,
      serviceRequestId,
      corporationId,
      componentMap,
      shareToken,
    ]
  );

  // Map visible steps to ProgressStepItem format (sidebar only shows visible steps)
  const progressSteps: ProgressStepItem[] = visibleSteps.map((step, index) => ({
    id: step.id,
    text: step.title,
    isCompleted: index < currentStep,
    disabled: false,
    isHidden: false,
  }));

  const handleStepChange = (stepIndex: number) => {
    // Allow backward navigation without validation
    if (stepIndex < currentStep) {
      setCurrentStep(stepIndex);
      return;
    }

    // For forward navigation, validate current step first
    if (stepIndex > currentStep) {
      const validationResult = validateCurrentStep();
      if (!validationResult.isValid) {
        revealFieldWithError(
          visibleSteps[currentStep],
          validationResult.firstErrorField
        );
        return;
      }
    }

    setCurrentStep(stepIndex);
  };

  // Next is disabled when the step would fail the validation the click runs anyway, so a blocked
  // step looks blocked. Repeatable modules keep entries under a save key, not flat field ids, so
  // their own renderer owns that check.
  const currentStepBlocked = useMemo(() => {
    const step = visibleSteps[currentStep];
    if (!step) return false;

    return (step.modules ?? []).some((module) => {
      if (
        module.visibility &&
        !isFieldVisible(module.visibility as ComponentVisibility)
      ) {
        return false;
      }
      if ((module.repeatable as { enabled?: boolean } | undefined)?.enabled) {
        return false;
      }
      return (module.components ?? []).some((component) => {
        if (
          component.visibility &&
          !isFieldVisible(component.visibility as ComponentVisibility)
        ) {
          return false;
        }
        const { fieldId } = component;
        if (!fieldId || notApplicableFields.has(fieldId)) return false;
        if (errors[fieldId]) return true;

        // Required only, and without touching state: validateField records what it finds, and
        // running that during render loops.
        const rules = (
          component.validation as { rules?: ValidationRules } | undefined
        )?.rules;
        if (!rules || !isRuleActive(rules.required, isFieldVisible)) {
          return false;
        }
        return isValueEmpty(formData[fieldId]);
      });
    });
  }, [
    visibleSteps,
    currentStep,
    errors,
    formData,
    isFieldVisible,
    notApplicableFields,
  ]);

  const handleNext = async () => {
    if (visibleSteps.length > 0 && currentStep < visibleSteps.length - 1) {
      setHasAttemptedSubmit(true);
      const validationResult = validateCurrentStep();
      if (!validationResult.isValid) {
        revealFieldWithError(
          visibleSteps[currentStep],
          validationResult.firstErrorField
        );
        return;
      }

      if (formTemplateId && !isPreview) {
        const savedId = await saveToBackend(FormsSubmissionStatus.DRAFT);
        if (savedId) {
          onDraftSaved?.(savedId);
        }
      }

      setCurrentStep(currentStep + 1);
    }
  };

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return; // bail if a submit is already in flight

    setHasAttemptedSubmit(true);
    // Validate all steps before submitting
    const validationResult = validateAllSteps();

    if (!validationResult.isValid) {
      // If error is in a different step, navigate to that step
      if (
        validationResult.errorStep !== undefined &&
        validationResult.errorStep !== currentStep
      ) {
        setCurrentStep(validationResult.errorStep);
      }

      revealFieldWithError(
        visibleSteps[validationResult.errorStep ?? currentStep],
        validationResult.firstErrorField
      );
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      // In preview mode skip all backend writes and just signal success.
      if (isPreview) {
        onSubmitSuccess?.('preview');
        return;
      }

      // A rejected Commenda OS write must not cost the customer their answers, so the
      // failures are collected and reported instead of aborting the submit. The list is
      // best-effort: paths inside submitToCommendaOS that only warn are not represented.
      const commendaOSResult = await submitToCommendaOS();

      // Run node-registered submit handlers — composite nodes whose value maps
      // to many DSL writes (e.g. Ownership → shareholdings). Each handler
      // sequences its own writes; handlers across nodes run in parallel.
      const handlerEntries = Array.from(submitHandlersRef.current.entries());
      const handlerResults = await Promise.allSettled(
        handlerEntries.map(([, handler]) => handler())
      );
      // A handler hands back the value the form should save. It does so on failure too, via the
      // rejection: writes it already made are in the database, and a value that does not record
      // them makes the retry repeat them rather than update them.
      const handlerFormDataOverrides = handlerResults.flatMap((result, index) => {
        const fieldId = handlerEntries[index]?.[0] ?? '';
        if (result.status === 'fulfilled') {
          return result.value != null ? [{ [fieldId]: result.value }] : [];
        }
        const partial = CommendaOSFailureHelper.partialValueFromRejection(result.reason);
        return partial != null ? [{ [fieldId]: partial }] : [];
      });
      const handlerRejections = handlerResults.flatMap((result, index) =>
        result.status === 'rejected'
          ? [
              {
                handlerFieldId: handlerEntries[index]?.[0] ?? 'unknown',
                reason: result.reason,
              },
            ]
          : []
      );

      const commendaOSFailures = CommendaOSFailureHelper.collectCommendaOSFailures(
        commendaOSResult.success ? [] : commendaOSResult.results,
        handlerRejections,
        'error' in commendaOSResult
          ? commendaOSResult.error?.message
          : undefined
      );

      if (commendaOSFailures.length > 0) {
        console.error(
          '[FORM_SUBMIT] Commenda OS writes failed:',
          commendaOSFailures
        );
      }

      const savedId = await saveToBackend(
        FormsSubmissionStatus.COMPLETED,
        commendaOSFailures,
        handlerFormDataOverrides.length > 0
          ? Object.assign({}, ...handlerFormDataOverrides)
          : undefined
      );

      if (!savedId) {
        console.error(
          '[FORM_SUBMIT] saveToBackend returned null — submission was not persisted'
        );
        showNotification({
          title: 'Submission failed',
          message: 'Your form could not be submitted. Please try again.',
          valid: false,
        });
        return;
      }

      // Answers are saved either way, but the task must not complete on top of a rejected write.
      // Handler rejections come first: ownership deletes before it recreates, so a partial failure
      // leaves the cap table short rather than a value merely missing.
      if (handlerRejections.length > 0) {
        showNotification({
          ...CommendaOSFailureHelper.getHandlerRejectionNotification(
            handlerRejections,
            componentMap
          ),
          valid: false,
        });
        return;
      }

      if (commendaOSFailures.length > 0) {
        showNotification({
          title: 'Some details were not saved',
          message: `Your answers are saved. ${CommendaOSFailureHelper.describeCommendaOSFailures(commendaOSFailures)} Please review and submit again.`,
          valid: false,
        });
        return;
      }

      if (storageKey) {
        try {
          localStorage.removeItem(storageKey);
        } catch {
          /* ignore */
        }
      }
      // Await so Submit stays disabled until the task-update + close flow ends.
      await onSubmitSuccess?.(savedId);
    } catch (error) {
      console.error('[FORM_SUBMIT] Error during submission:', error);
      showNotification({
        title: 'Submission failed',
        message: 'An unexpected error occurred. Please try again.',
        valid: false,
      });
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const isLastStep =
    visibleSteps.length === 0 || currentStep === visibleSteps.length - 1;

  const clearFieldError = useCallback((fieldName: string) => {
    setErrors((prev) => {
      if (!(fieldName in prev)) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  const getFieldError = useCallback(
    (fieldName: string) => errors[fieldName],
    [errors]
  );

  const expandCurrentStepModules = useCallback(() => {
    const stepModules = visibleSteps[currentStep]?.modules ?? [];
    setExpandedModules(new Set(stepModules.map((module) => module.id)));
  }, [visibleSteps, currentStep]);

  const previewTestRunner = usePreviewTestRunner({
    isPreview: Boolean(isPreview),
    visibleSteps,
    currentStep,
    setCurrentStep,
    formData,
    dslContext: effectiveDslContext,
    updateField,
    validateField,
    isFieldVisible,
    isFieldDisabled,
    validateCurrentStep,
    componentMap,
    getPreviewFillHandler,
    isCommendaOSPrefilling: isPrefilling,
    clearFieldError,
    getFieldError,
    expandCurrentStepModules,
  });

  // A host (the builder's Runs tab) can drive a run instead of the panel.
  const hasAutoStartedRef = useRef(false);
  useEffect(() => {
    if (!isPreview || !autoStartTestRun || hasAutoStartedRef.current) return;
    if (visibleSteps.length === 0) return;
    hasAutoStartedRef.current = true;
    previewTestRunner.runToEnd();
  }, [isPreview, autoStartTestRun, visibleSteps.length, previewTestRunner]);

  const reportedRunRef = useRef(false);
  useEffect(() => {
    const { runStatus, runReport } = previewTestRunner;
    if (runStatus === 'running' || runStatus === 'idle') {
      reportedRunRef.current = false;
      return;
    }
    if (reportedRunRef.current || !runReport) return;
    reportedRunRef.current = true;
    onTestRunComplete?.(runReport, runStatus);
  }, [previewTestRunner, onTestRunComplete]);

  const previewTestingPanel =
    // A linked form open in its drawer shows its own panel in the same spot.
    isPreview && !hideTestingPanel && !nestedFormOpen ? (
      <PreviewTestingPanel
        onFillStep={previewTestRunner.fillCurrentStep}
        onRunToEnd={previewTestRunner.runToEnd}
        onStop={previewTestRunner.stopRun}
        runStatus={previewTestRunner.runStatus}
        lastFillResult={previewTestRunner.lastFillResult}
        runReport={previewTestRunner.runReport}
        activityLabel={previewTestRunner.activityLabel}
      />
    ) : null;

  if (shouldUseContainer) {
    return (
      <ComponentRegistryProvider value={componentRegistry}>
        <FormContext.Provider value={contextValue}>
          {previewTestingPanel}
          <div className="flex flex-col">
            <div
              className={classNames(
                'w-full overflow-y-auto hide-scrollbar',
                className
              )}
            >
              {currentStepData?.modules?.map((module) => {
                if (
                  module.visibility &&
                  !isFieldVisible(module.visibility as ComponentVisibility)
                ) {
                  return null;
                }
                return (module.repeatable as { enabled?: boolean })?.enabled ? (
                  <div
                    key={module.id}
                    className="mb-8 bg-bg-primary rounded-xl p-5"
                  >
                    <RepeatableModuleRenderer
                      module={module as FormModule}
                      componentRegistry={componentRegistry}
                      parentContext={contextValue}
                      isFieldVisible={isFieldVisible}
                    />
                  </div>
                ) : (
                  <div
                    key={module.id}
                    className="mb-8 bg-bg-primary rounded-xl p-5"
                  >
                    {module.name && (
                      <h3 className="txt-large-medium mb-2">{module.name}</h3>
                    )}
                    {module.description && (
                      <p className="txt-small-regular text-text-tertiary mb-4">
                        {module.description}
                      </p>
                    )}

                    <div className="space-y-4">
                      {module.components
                        .slice()
                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                        .map((component, index) => {
                          if (!isFieldVisible(component.visibility))
                            return null;

                          const Component =
                            componentRegistry[component.component];
                          if (!Component) {
                            console.warn(
                              `Component ${component.component} not found`
                            );
                            return null;
                          }

                          if (component.component === 'FormSummary') {
                            const summaryModules = dsl?.steps?.flatMap((step) =>
                              step.modules
                                .filter(
                                  (m) =>
                                    !m.visibility ||
                                    isFieldVisible(
                                      m.visibility as ComponentVisibility
                                    )
                                )
                                .map((m) => ({
                                  id: m.id,
                                  name: m.name,
                                  description: m.description,
                                  saveKey: m.saveKey,
                                  repeatable: (
                                    m.repeatable as { enabled?: boolean }
                                  )?.enabled,
                                  fieldIds: m.components
                                    .map((c) => c.fieldId)
                                    .filter(
                                      (fid): fid is string =>
                                        typeof fid === 'string'
                                    ),
                                }))
                            );

                            const summaryProps = {
                              ...(component.props || {}),
                              modules: summaryModules,
                              fieldLabelMap,
                              componentMap,
                              corporationId,
                            };

                            return (
                              <div
                                key={
                                  component.id ||
                                  component.fieldId ||
                                  `${component.component}-${index}`
                                }
                              >
                                <Component
                                  {...component}
                                  props={summaryProps}
                                />
                              </div>
                            );
                          }

                          return (
                            <div
                              key={
                                component.id ||
                                component.fieldId ||
                                `${component.component}-${index}`
                              }
                            >
                              <Component {...component} />
                            </div>
                          );
                        })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="border-t p-xl flex items-center gap-lg justify-end">
              <Button variant="secondary" onClick={onClose}>
                <span>Close</span>
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={isSubmitting || uploadingFields.size > 0}
              >
                <span>{isSubmitting ? 'Submitting...' : 'Save'}</span>
              </Button>
            </div>
          </div>
        </FormContext.Provider>
      </ComponentRegistryProvider>
    );
  }

  const shouldShowHeader = !hideTopHeader;

  if (existingSubmissionId && existingSubmissionError) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-4 px-6 py-12 text-center">
        <Icons.AlertCircle className="h-12 w-12 text-text-error" />
        <div className="space-y-2">
          <p className="txt-medium-medium text-text-primary">
            Failed to load saved form
          </p>
          <p className="txt-small-regular text-text-tertiary">
            This saved form could not be loaded. You may not have access to it,
            or it may no longer exist.
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => retryExistingSubmission()}>
            Retry
          </Button>
          {onClose ? <Button onClick={onClose}>Close</Button> : null}
        </div>
      </div>
    );
  }

  return (
    <ComponentRegistryProvider value={componentRegistry}>
      <FormContext.Provider value={contextValue}>
        {previewTestingPanel}
        <NewIncorporationLayout
          title={dsl?.form?.title ?? 'Form'}
          subtitle={
            (dsl?.form?.settings as Record<string, unknown> | undefined)
              ?.description as string | undefined
          }
          steps={progressSteps}
          activeStepIndex={currentStep}
          onStepClick={handleStepChange}
          hideSidebar={visibleSteps.length <= 1}
          hideHeader={hideTitle}
          hideTopHeader={!shouldShowHeader}
          header={
            shouldShowHeader
              ? {
                  entity: {
                    name: header?.entityName,
                    countryCode: header?.countryCode as CountryCode,
                    onClick: () => router.push('/'),
                  },
                  leftSlot: header?.leftSlot,
                  hideCloseButton: header?.hideCloseButton ?? false,
                  onShare: header?.hideShareButton
                    ? undefined
                    : header?.onShare,
                  onClose,
                }
              : undefined
          }
          formActions={{
            onBack:
              currentStep > 0
                ? () => setCurrentStep(currentStep - 1)
                : undefined,
            onNext: isLastStep ? handleSubmit : handleNext,
            backLabel: 'Back',
            nextLabel: isLastStep ? 'Submit' : 'Next',
            disableBack: currentStep === 0,
            disableNext:
              isSaving ||
              isSubmitting ||
              isLoadingSubmission ||
              uploadingFields.size > 0 ||
              currentStepBlocked,
            isLoading: isSubmitting,
            hideBack: currentStep === 0,
          }}
        >
          {currentStepData?.modules?.map((module, moduleIndex) => {
            if (
              module.visibility &&
              !isFieldVisible(module.visibility as ComponentVisibility)
            ) {
              return null;
            }
            const sortedComponents = module.components
              .slice()
              .sort((a, b) => (a.order || 0) - (b.order || 0));
            const visibleComponents = sortedComponents.filter((c) =>
              isFieldVisible(c.visibility)
            );
            const lastVisibleComponentIndex = visibleComponents.length - 1;

            // If all components are hidden by visibility conditions, hide the entire module
            if (visibleComponents.length === 0) {
              return null;
            }

            const isRepeatable = (module.repeatable as { enabled?: boolean })
              ?.enabled;
            const isExpanded = expandedModules.has(module.id);

            // NestedForm-only: when the only data component is a single NestedForm, skip section wrapper.
            const nestedForms = visibleComponents.filter(
              (c) => c.component === 'NestedForm'
            );
            const otherWithFieldId = visibleComponents.filter(
              (c) => c.fieldId && c.component !== 'NestedForm'
            );
            const isNestedFormOnly =
              nestedForms.length === 1 && otherWithFieldId.length === 0;

            // For repeatable modules, use CollapsibleModuleCard unless NestedForm-only
            if (isRepeatable && !isNestedFormOnly) {
              return (
                <CollapsibleModuleCard
                  key={module.id}
                  title={module.name}
                  description={module.description}
                  isExpanded={isExpanded}
                  onExpandedChange={(expanded) =>
                    handleModuleExpandChange(module.id, expanded)
                  }
                >
                  <RepeatableModuleRenderer
                    module={module as FormModule}
                    componentRegistry={componentRegistry}
                    parentContext={contextValue}
                    isFieldVisible={isFieldVisible}
                  />
                </CollapsibleModuleCard>
              );
            }

            // Repeatable but NestedForm-only: render without section wrapper
            if (isRepeatable && isNestedFormOnly) {
              return (
                <div key={module.id}>
                  <RepeatableModuleRenderer
                    module={module as FormModule}
                    componentRegistry={componentRegistry}
                    parentContext={contextValue}
                    isFieldVisible={isFieldVisible}
                  />
                </div>
              );
            }

            // Non-repeatable NestedForm-only: render without section wrapper
            if (isNestedFormOnly) {
              const nestedComp = nestedForms[0];
              const keyId = (nestedComp.props as { keyId?: string } | undefined)
                ?.keyId;
              if (!keyId) {
                return null;
              }
              const NestedComp = componentRegistry['NestedForm'];
              if (NestedComp) {
                return (
                  <div key={module.id}>
                    <NestedComp
                      key={nestedComp.id ?? nestedComp.fieldId ?? 'nested'}
                      {...nestedComp}
                    />
                  </div>
                );
              }
            }

            // For non-repeatable modules, use CollapsibleModuleCard
            return (
              <CollapsibleModuleCard
                key={module.id}
                title={module.name}
                description={module.description}
                isExpanded={isExpanded}
                onExpandedChange={(expanded) =>
                  handleModuleExpandChange(module.id, expanded)
                }
              >
                <div className="grid grid-cols-6 gap-x-4 gap-y-6 w-full">
                  {sortedComponents.map((component, index) => {
                    if (!isFieldVisible(component.visibility)) return null;

                    const Component = componentRegistry[component.component];
                    if (!Component) {
                      console.warn(
                        `Component ${component.component} not found`
                      );
                      return null;
                    }

                    // Check if this is the last visible component in the module
                    const visibleIndex = visibleComponents.findIndex(
                      (c) =>
                        (c.id || c.fieldId || `${c.component}-${index}`) ===
                        (component.id ||
                          component.fieldId ||
                          `${component.component}-${index}`)
                    );
                    const isLastComponent =
                      visibleIndex === lastVisibleComponentIndex;

                    if (component.component === 'FormSummary') {
                      const summaryModules = dsl?.steps?.flatMap((step) =>
                        step.modules
                          .filter(
                            (m) =>
                              !m.visibility ||
                              isFieldVisible(
                                m.visibility as ComponentVisibility
                              )
                          )
                          .map((m) => ({
                            id: m.id,
                            name: m.name,
                            description: m.description,
                            saveKey: m.saveKey,
                            repeatable: (m.repeatable as { enabled?: boolean })
                              ?.enabled,
                            fieldIds: m.components
                              .map((c) => c.fieldId)
                              .filter(
                                (fid): fid is string => typeof fid === 'string'
                              ),
                          }))
                      );

                      const summaryProps = {
                        ...(component.props || {}),
                        modules: summaryModules,
                        fieldLabelMap,
                        componentMap,
                        corporationId: corporationId,
                      };

                      return (
                        <div
                          key={
                            component.id ||
                            component.fieldId ||
                            `${component.component}-${index}`
                          }
                          className={fieldWidthSpan(component.width)}
                        >
                          <Component {...component} props={summaryProps} />
                        </div>
                      );
                    }

                    return (
                      <div
                        key={
                          component.id ||
                          component.fieldId ||
                          `${component.component}-${index}`
                        }
                        className={fieldWidthSpan(component.width)}
                        onFocus={() => {
                          if (isLastComponent) {
                            handleFieldFocus(module.id, true);
                          }
                        }}
                      >
                        <Component {...component} />
                      </div>
                    );
                  })}
                </div>
              </CollapsibleModuleCard>
            );
          })}
        </NewIncorporationLayout>
      </FormContext.Provider>
    </ComponentRegistryProvider>
  );
};

// =============================================================================
// useFormRenderer Hook - For custom usage
// =============================================================================

export interface UseFormRendererOptions {
  dsl: DSLFile;
  initialData?: Record<string, unknown>;
  /** DSL context for Commenda OS operations */
  dslContext?: FormDSLContext;
}

export function useFormRenderer({
  dsl,
  initialData = {},
  dslContext,
}: UseFormRendererOptions) {
  const bindingsMap = useMemo(() => collectBindings(dsl), [dsl]);
  const disabledBindings = useMemo(() => collectDisabledBindings(dsl), [dsl]);
  const defaultValues = useMemo(
    () => collectDefaultValues(dsl, dslContext as Record<string, unknown>),
    [dsl, dslContext]
  );
  const componentMap = useMemo(() => buildComponentMap(dsl), [dsl]);

  const userTouchedFieldsRef = useRef<Set<string>>(new Set());

  const [formData, setFormData] = useState<Record<string, unknown>>({
    ...defaultValues,
    ...initialData,
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isFieldVisibleRef = useRef<(v?: ComponentVisibility) => boolean>(
    () => true
  );

  const isFieldDisabled = useCallback(
    (fieldName?: string): boolean => {
      if (!fieldName) return false;
      return disabledBindings.has(fieldName);
    },
    [disabledBindings]
  );

  const updateField = useCallback(
    (fieldName: string, value: unknown) => {
      userTouchedFieldsRef.current.add(fieldName);
      setFormData((prev) => {
        const updated = { ...prev, [fieldName]: value };

        const boundTargets = bindingsMap.get(fieldName);
        if (boundTargets) {
          boundTargets.forEach((targetField) => {
            updated[targetField] = value;
          });
        }

        return updated;
      });
    },
    [bindingsMap]
  );

  const defaultExpressionDependents = useMemo(() => {
    const list: { targetFieldId: string; sourceFieldId: string }[] = [];
    Object.entries(componentMap).forEach(([fieldId, component]) => {
      const sourceId = component.defaultExpression
        ? fieldTokenId(component.defaultExpression)
        : undefined;
      if (sourceId)
        list.push({ targetFieldId: fieldId, sourceFieldId: sourceId });
    });
    return list;
  }, [componentMap]);

  useEffect(() => {
    if (defaultExpressionDependents.length === 0) return;
    setFormData((prev) => {
      let next: Record<string, unknown> | null = null;
      for (const {
        targetFieldId,
        sourceFieldId,
      } of defaultExpressionDependents) {
        if (userTouchedFieldsRef.current.has(targetFieldId)) continue;
        const v = prev[sourceFieldId];
        if (v === undefined) continue;
        const targetValue = prev[targetFieldId];
        if (targetValue != null && targetValue !== '') continue;
        if (!next) next = { ...prev };
        next[targetFieldId] = v;
      }
      return next ?? prev;
    });
  }, [defaultExpressionDependents]);

  const validateField = useCallback(
    (
      fieldName: string,
      value: unknown,
      rules?: ValidationRules
    ): string | null => {
      const error = fieldValidationError(value, rules, {
        isFieldVisible: isFieldVisibleRef.current,
        resolveField: (field) =>
          field.includes('.') ? getFormValueByPath(formData, field) : formData[field],
      });
      if (error) {
        setErrors((prev) => ({ ...prev, [fieldName]: error }));
        return error;
      }

      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
      return null;
    },
    [formData]
  );

  const isFieldVisible = useCallback(
    (visibility?: ComponentVisibility): boolean => {
      if (!visibility) return true;

      const { condition } = visibility;

      if (
        condition.field &&
        condition.operator &&
        (condition.value !== undefined || condition.operator === 'isNotEmpty')
      ) {
        const fieldValue = condition.field.startsWith('context:')
          ? dslContext?.[condition.field.slice('context:'.length)]
          : condition.field.includes('.')
            ? getFormValueByPath(formData, condition.field)
            : formData[condition.field];
        const fieldComponent = componentMap[condition.field];

        // Handle CheckboxGroup fields: values are stored as Record<string, boolean>
        if (
          fieldComponent?.component === 'CheckboxGroup' &&
          typeof fieldValue === 'object' &&
          fieldValue !== null &&
          !Array.isArray(fieldValue)
        ) {
          const checkboxValue = fieldValue as Record<string, boolean>;
          const optionKey = String(condition.value);

          switch (condition.operator) {
            case 'equals':
              return checkboxValue[optionKey] === true;
            case 'notEquals':
              return checkboxValue[optionKey] !== true;
            case 'contains':
              // Check if any checked option matches the condition value
              return Object.entries(checkboxValue).some(
                ([key, checked]) => checked === true && key === optionKey
              );
            case 'isNotEmpty':
              return Object.values(checkboxValue).some(
                (checked) => checked === true
              );
            default:
              return true;
          }
        }

        // Default behavior for non-checkbox fields
        switch (condition.operator) {
          case 'equals':
            return fieldValue === condition.value;
          case 'notEquals':
            return fieldValue !== condition.value;
          case 'greaterThan':
            return Number(fieldValue) > Number(condition.value);
          case 'lessThan':
            return Number(fieldValue) < Number(condition.value);
          case 'contains':
            if (Array.isArray(fieldValue)) {
              return fieldValue.some((item) => item === condition.value);
            }
            return String(fieldValue ?? '').includes(String(condition.value));
          case 'isNotEmpty':
            return Array.isArray(fieldValue)
              ? fieldValue.length > 0
              : fieldValue !== null &&
                  fieldValue !== undefined &&
                  fieldValue !== '';
          default:
            return true;
        }
      }

      // Handle AND conditions - all must be true
      if (condition.and) {
        return condition.and.every((cond) =>
          isFieldVisible({ condition: cond })
        );
      }

      // Handle OR conditions - at least one must be true
      if (condition.or) {
        return condition.or.some((cond) => isFieldVisible({ condition: cond }));
      }

      return true;
    },
    [formData, componentMap, dslContext]
  );

  useEffect(() => {
    isFieldVisibleRef.current = isFieldVisible;
  }, [isFieldVisible]);

  const contextValue: FormContextType = useMemo(
    () => ({
      formData,
      setFormData,
      updateField,
      currentStep,
      setCurrentStep,
      errors,
      setErrors,
      validateField,
      isFieldVisible,
      isFieldDisabled,
      dslContext,
      nestedFormOpen: null,
      setNestedFormOpen: () => {},
      uploadingFields: new Set<string>(),
      setFieldUploading: () => {},
      componentMap,
    }),
    [
      formData,
      updateField,
      currentStep,
      errors,
      validateField,
      isFieldVisible,
      isFieldDisabled,
      dslContext,
      componentMap,
    ]
  );

  return {
    formData,
    setFormData,
    updateField,
    currentStep,
    setCurrentStep,
    errors,
    setErrors,
    validateField,
    isFieldVisible,
    isFieldDisabled,
    dslContext,
    contextValue,
    dsl,
  };
}
